import React, { useEffect, useState, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import loadable from '@loadable/component';
import useConfig from '../../../../hooks/useConfig';
import { fetchCMSStaticPageIfNeeded } from '../../../../redux/cms/actions';
import StateInterface, { BellaThunkDispatch } from '../../../../redux/types';
import { getOverlayById } from '../../../../redux/overlays/selectors';
import { hideOverlay, exitContentWithAnimation, showOverlay } from '../../../../redux/overlays/actions';
import { RegisterUserValues, ValidationErrors, ClubRegistrationStatus } from '../../../../redux/user/types';
import { apiRegisterUserToClub } from '../../../../redux/user/actions';
import { AnimationEntryExit, ShopToClubOverlay, OverlayId } from '../../types';
import useTranslator from '../../../../hooks/useTranslator';
import useClubRegistrationPopup from '../../../../hooks/useClubRegistrationPopup';
import { OVERLAY_ID as LOGIN_AND_REGISTER_OVERLAY_ID } from '../../LoginAndRegisterPopUp';
import useGa from '../../../../hooks/useGa';
import { addNotification } from '../../../../redux/notifications/actions';
import GaTracking from '../../../../ga';
import { ShopToClubRegistrationResolutions } from '../../../../ga/shopToClub';
import { updateClubRegistrationStatus, apiUserExists } from '../../../../redux/user/clubRegistration/actions';
import useNeverShowShopToClub from '../../../../hooks/flags/useNeverShowShopToClub';
import { DOUBLE_OPT_IN_OVERLAY } from '../DoubleOptIn';

const SingleStepShopToClub = loadable(() => import('./SingleStepPopUp'));
const MultiStepShopToClub = loadable(() => import('./MultiStepPopUp'));

export const SHOW_LOGIN_FORM_CB_IDENTIFIER = 'showLoginForm';
export const PRIVACY_POLICY_STATIC_PAGE_KEY = 'privacy_policy';

export const REGISTRATION_STEP_ONE = 'REGISTRATION_STEP_ONE';
export const REGISTRATION_STEP_TWO = 'REGISTRATION_STEP_TWO';

export type RegistrationStep = typeof REGISTRATION_STEP_ONE | typeof REGISTRATION_STEP_TWO;

interface Props {
    isClubRegistrationActive: boolean;
    mobileImage: string;
    topBorderColor: string;
    validationErrors: ValidationErrors | {};
    contentAnimate: AnimationEntryExit;
    registrationStatus?: ClubRegistrationStatus;
    isDismissable: boolean;
    isUndismissableFromOutsideClick: boolean;
}

interface ShopToClubOverlayProps {
    overlayId: ShopToClubOverlay;
}

interface NotificationMessages {
    newUser: string;
    existingUser: string;
}

interface RegistrationSuccessOptions {
    notificationMessages: NotificationMessages;
    status: ClubRegistrationStatus;
    showDOIOverlay: boolean;
}

export const trackRegistrationSuccess = (gaTracker: GaTracking, status: ClubRegistrationStatus) => {
    const label =
        status === ClubRegistrationStatus.NEW_REGISTRATION
            ? ShopToClubRegistrationResolutions.New
            : ShopToClubRegistrationResolutions.Login;

    gaTracker.shopToClub.trackRegistration(label);
};

export const handlePrivacyPolicyAcceptanceTracking = (
    overlayId: string,
    gaTracker: GaTracking,
    formValues: RegisterUserValues
) => {
    if (overlayId === OverlayId.SHOP_TO_CLUB_REGISTRATION_MULTI_STEP_OVERLAY) {
        const label = formValues.isClubNewsletterSubscribed ? 'accepted' : 'not accepted';

        gaTracker.shopToClub.trackPrivacyPolicyAcceptance(label);
    }
};

export const handleClubRegistrationSuccess = (
    dispatch: BellaThunkDispatch,
    gaTracker: GaTracking,
    options: RegistrationSuccessOptions,
    overlayId: ShopToClubOverlay
    // eslint-disable-next-line max-params
) => {
    const { notificationMessages, showDOIOverlay } = options;

    trackRegistrationSuccess(gaTracker, options.status);
    dispatch(exitContentWithAnimation(overlayId));

    if (showDOIOverlay) {
        dispatch(showOverlay(DOUBLE_OPT_IN_OVERLAY));
    } else {
        dispatch(addNotification(notificationMessages.newUser));
    }
};

const ConnectedShopToClub: React.SFC<ShopToClubOverlayProps & Props> = ({
    isClubRegistrationActive,
    validationErrors,
    contentAnimate,
    mobileImage,
    topBorderColor,
    registrationStatus,
    isDismissable,
    overlayId,
    isUndismissableFromOutsideClick,
}) => {
    const gaTracker = useGa();
    const [onDismissCallbacks, setOnDismissCallbacks] = useState({});
    const [registrationStep, setRegistrationStep] = useState<RegistrationStep>(REGISTRATION_STEP_ONE);
    const addOnDismissCallback = (identifier: string) =>
        setOnDismissCallbacks({
            ...onDismissCallbacks,
            [identifier]: true,
        });
    const [, setNeverShowShopToClub] = useNeverShowShopToClub();
    useClubRegistrationPopup();
    const dispatch = useDispatch();
    const translator = useTranslator();
    const config = useConfig();
    const isCountryDe = config.countryCode.indexOf('de-DE') > -1;
    const newUser = translator('Rocket_Service_Message::CUSTOMER_REGISTRATION_SUCCESS');
    const existingUser = translator('Thank you! You are already a Westwing member.');

    const handleDismissClick = () => {
        gaTracker.shopToClub.trackOverlayDismissed();
        dispatch(exitContentWithAnimation(overlayId));
    };

    const handleAnimationEnd = useCallback(() => {
        dispatch(hideOverlay(overlayId));
        dispatch(updateClubRegistrationStatus());

        if (onDismissCallbacks[SHOW_LOGIN_FORM_CB_IDENTIFIER]) {
            dispatch(
                showOverlay(LOGIN_AND_REGISTER_OVERLAY_ID, {
                    isLogin: true,
                })
            );
        }

        setOnDismissCallbacks({});
    }, [dispatch, onDismissCallbacks, overlayId]);

    useEffect(() => {
        if (isClubRegistrationActive) {
            if (overlayId === OverlayId.SHOP_TO_CLUB_REGISTRATION_MULTI_STEP_OVERLAY) {
                dispatch(fetchCMSStaticPageIfNeeded(PRIVACY_POLICY_STATIC_PAGE_KEY));
            }
            gaTracker.shopToClub.trackOverlayShown();
        }
    }, [dispatch, gaTracker.shopToClub, isClubRegistrationActive, overlayId]);

    useEffect(() => {
        if (registrationStatus && isClubRegistrationActive) {
            if (registrationStatus !== ClubRegistrationStatus.WRONG_PASSWORD) {
                const isNewUser = registrationStatus === ClubRegistrationStatus.NEW_REGISTRATION;
                const isNewsLetterNotSubscribed =
                    registrationStatus === ClubRegistrationStatus.EXISTING_USER_NOT_SUBSCRIBED;
                const showDOIOverlay = isCountryDe && (isNewsLetterNotSubscribed || isNewUser);

                handleClubRegistrationSuccess(
                    dispatch,
                    gaTracker,
                    {
                        notificationMessages: { existingUser, newUser },
                        status: registrationStatus,
                        showDOIOverlay,
                    },
                    overlayId
                );
            } else {
                // never show2club overlay to user again
                setNeverShowShopToClub();
                dispatch(exitContentWithAnimation(overlayId));
                dispatch(addNotification(existingUser));
                gaTracker.shopToClub.trackRegistration(ShopToClubRegistrationResolutions.WrongPassword);
            }
        }
    }, [
        dispatch,
        existingUser,
        gaTracker,
        isClubRegistrationActive,
        isCountryDe,
        newUser,
        overlayId,
        registrationStatus,
        setNeverShowShopToClub,
    ]);

    const handleRegistration = (formValues: RegisterUserValues) => {
        dispatch(apiRegisterUserToClub(formValues));
        handlePrivacyPolicyAcceptanceTracking(overlayId, gaTracker, formValues);
    };

    const handleLoginSwitch = () => {
        handleDismissClick();
        addOnDismissCallback(SHOW_LOGIN_FORM_CB_IDENTIFIER);
    };

    const nextRegistrationStep = () => setRegistrationStep(REGISTRATION_STEP_TWO);
    const trackPrivacyPolicyShown = () => gaTracker.shopToClub.trackPrivacyPolicyShown();

    const handleCheckIfUserExists = (email: string, password: string) => {
        dispatch(apiUserExists(email, password, nextRegistrationStep, trackPrivacyPolicyShown));
    };

    if (isClubRegistrationActive && overlayId === OverlayId.SHOP_TO_CLUB_REGISTRATION_SINGLE_STEP_OVERLAY) {
        return (
            <SingleStepShopToClub
                handleOnAnimationEnd={handleAnimationEnd}
                handleRegistration={handleRegistration}
                handleLoginSwitch={handleLoginSwitch}
                handleDismissClick={handleDismissClick}
                mobileImage={mobileImage}
                topBorderColor={topBorderColor}
                contentAnimate={contentAnimate}
                validationErrors={validationErrors}
                isDismissable={isDismissable}
                isUndismissisableFromOutsideClick={isUndismissableFromOutsideClick}
            />
        );
    }
    if (isClubRegistrationActive && overlayId === OverlayId.SHOP_TO_CLUB_REGISTRATION_MULTI_STEP_OVERLAY) {
        return (
            <MultiStepShopToClub
                handleOnAnimationEnd={handleAnimationEnd}
                handleRegistration={handleRegistration}
                handleLoginSwitch={handleLoginSwitch}
                handleDismissClick={handleDismissClick}
                mobileImage={mobileImage}
                topBorderColor={topBorderColor}
                contentAnimate={contentAnimate}
                isDismissable={isDismissable}
                registrationStep={registrationStep}
                handleCheckIfUserExists={handleCheckIfUserExists}
                isUndismissisableFromOutsideClick={isUndismissableFromOutsideClick}
            />
        );
    }
    return null;
};

const mapStateToProps = (state: StateInterface, ownProps: ShopToClubOverlayProps): Props => {
    const { mobileImage, topBorderColor, isDismissable, isUndismissableFromOutsideClick } = state.configurations
        .shopToClub || {
        mobileImage: '',
        topBorderColor: '',
        isDismissable: true,
        isUndismissableFromOutsideClick: false,
    };
    const { error: validationErrors } = state.login;
    const { isActive: isClubRegistrationActive, contentAnimate = AnimationEntryExit.ENTRY } = getOverlayById(
        state.overlays,
        ownProps.overlayId
    );
    const registrationStatus = state.clubRegistration.status;

    return {
        isClubRegistrationActive,
        validationErrors,
        contentAnimate,
        mobileImage,
        topBorderColor,
        registrationStatus,
        isDismissable,
        isUndismissableFromOutsideClick,
    };
};

export default connect(mapStateToProps)(ConnectedShopToClub);
